// Shared CTE completion analysis utilities.
// Previously duplicated across Dashboard / Students / Reports pages.

export function buildStudentRecords(studentSnapshot) {
  const records = {};
  const gradYearSet = new Set();
  const schoolYearSet = new Set();

  studentSnapshot.forEach(doc => {
    const data = doc.data();
    const studentId = data.studentId;
    const schoolYear = data.schoolYear || '';

    if (schoolYear) schoolYearSet.add(schoolYear);

    if (!records[studentId]) {
      records[studentId] = {
        id: studentId,
        name: data.studentName,
        gradYear: data.gradYear ? data.gradYear.toString() : null,
        courses: [],
        years: {}
      };
      if (data.gradYear) gradYearSet.add(data.gradYear.toString());
    }

    if (data.courses && Array.isArray(data.courses)) {
      records[studentId].courses = [
        ...records[studentId].courses,
        ...data.courses.map(course => ({ ...course, year: schoolYear }))
      ];
      records[studentId].years[schoolYear] = data.courses;
    }
  });

  const sortYears = set => Array.from(set).sort((a, b) => {
    if (!isNaN(a) && !isNaN(b)) return parseInt(a, 10) - parseInt(b, 10);
    return a.localeCompare(b);
  });

  return {
    students: Object.values(records),
    gradYears: sortYears(gradYearSet),
    schoolYears: sortYears(schoolYearSet)
  };
}

export function analyzeStudentCompletion(student, program, currentYear, currentSemester) {
  const programCoursesTaken = student.courses.filter(course =>
    program.courses && program.courses.includes(course.courseName)
  );

  const uniqueCourses = new Set(programCoursesTaken.map(c => c.courseName));
  const courseCount = uniqueCourses.size;

  let totalCredits = 0;
  let upperLevelCredits = 0;
  let hasUpperLevelCourse = false;
  let projectedTotalCredits = 0;
  let projectedUpperLevelCredits = 0;
  let willHaveUpperLevelCourse = false;

  const coursesByName = {};
  programCoursesTaken.forEach(course => {
    if (!coursesByName[course.courseName]) coursesByName[course.courseName] = [];
    coursesByName[course.courseName].push(course);
  });

  Object.keys(coursesByName).forEach(courseName => {
    const instances = coursesByName[courseName];
    let actualCredits = 0;
    let projectedCredits = 0;
    const isUpperLevel = program.upperLevelCourses?.includes(courseName);

    instances.forEach(course => {
      const earned = parseFloat(course.earnedCredits) || 0;
      actualCredits += earned;

      let projected = earned;
      if (course.year === currentYear) {
        if (course.courseLength === 'YR') {
          if (currentSemester === '1') {
            // Mid-year: if a sem 1 grade is already posted (earned > 0) assume
            // the student finishes the year → double it. If no grade is posted
            // yet (earned === 0) assume a full-year completion (1.0). The prior
            // implementation projected 0 in this case, hiding active students
            // from potential-completer reports.
            projected = projected > 0 ? projected * 2 : 1;
          } else if (currentSemester === '2' && projected < 1 && projected > 0) {
            projected = 1;
          }
        } else if (course.courseLength === 'SM2' && currentSemester === '1' && projected === 0) {
          projected = 0.5;
        }
      }
      projectedCredits += projected;
    });

    totalCredits += actualCredits;
    projectedTotalCredits += projectedCredits;

    if (isUpperLevel) {
      upperLevelCredits += actualCredits;
      projectedUpperLevelCredits += projectedCredits;
      if (actualCredits > 0) hasUpperLevelCourse = true;
      if (projectedCredits > 0) willHaveUpperLevelCourse = true;
    }
  });

  const isLocalOnly = program.approvalType === 'local';

  const meetsCompleterCriteria =
    courseCount >= 3 && totalCredits >= 4 && hasUpperLevelCourse;
  const isCompleter = meetsCompleterCriteria && !isLocalOnly;

  const meetsProjectedCompleterCriteria =
    !isCompleter &&
    courseCount >= 3 &&
    projectedTotalCredits >= 4 &&
    willHaveUpperLevelCourse;
  const willBeCompleter = meetsProjectedCompleterCriteria && !isLocalOnly;

  const meetsConcentratorCriteria =
    !isCompleter && !willBeCompleter && courseCount >= 2 && totalCredits >= 2;
  const isConcentrator = meetsConcentratorCriteria && !isLocalOnly;

  const isExplorer =
    (isLocalOnly && (meetsCompleterCriteria || meetsProjectedCompleterCriteria || meetsConcentratorCriteria)) ||
    (!isLocalOnly && !isCompleter && !willBeCompleter && !isConcentrator && courseCount >= 2 && totalCredits >= 2);

  const isParticipant =
    !isCompleter && !willBeCompleter && !isConcentrator && !isExplorer && courseCount > 0;

  const isNotCTE = courseCount === 0;

  const gaps = [];
  if (courseCount < 3) gaps.push(`Needs ${3 - courseCount} more course(s)`);
  if (totalCredits < 4) gaps.push(`Needs ${(4 - totalCredits).toFixed(1)} more credit(s)`);
  if (!hasUpperLevelCourse) gaps.push('Needs at least one upper level course');
  if (isLocalOnly && (meetsCompleterCriteria || meetsProjectedCompleterCriteria || meetsConcentratorCriteria)) {
    gaps.push('Program is not regionally approved');
  }

  return {
    isCompleter,
    isConcentrator,
    isExplorer,
    isParticipant,
    isNotCTE,
    willBeCompleter,
    courseCount,
    totalCredits,
    upperLevelCredits,
    hasUpperLevelCourse,
    projectedTotalCredits,
    projectedUpperLevelCredits,
    willHaveUpperLevelCourse,
    gaps,
    coursesTaken: programCoursesTaken
  };
}

export function getCTECode(status) {
  if (!status) return { code: 'Code 4 (Not CTE)', colorClass: 'text-gray-600' };
  if (status.isCompleter) return { code: 'Code 7 (Completer)', colorClass: 'text-green-600' };
  if (status.willBeCompleter) return { code: 'Will be Code 7 (Completer)', colorClass: 'text-green-400' };
  if (status.isConcentrator) return { code: 'Code 6 (Concentrator)', colorClass: 'text-blue-600' };
  if (status.isExplorer) return { code: 'Code E (Explorer)', colorClass: 'text-purple-600' };
  if (status.isParticipant) return { code: 'Code 5 (Participant)', colorClass: 'text-orange-600' };
  return { code: 'Code 4 (Not CTE)', colorClass: 'text-gray-600' };
}
